<template>
  <div class="space-y-6">
    <!-- 基本信息 -->
    <div>
      <h4 class="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">基本信息</h4>
      <div class="grid grid-cols-2 gap-4">
        <div class="space-y-2">
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">用户名</label>
          <input type="text" :value="user?.username" disabled
            class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-gray-50 dark:bg-gray-600 text-gray-500 dark:text-gray-300" />
        </div>
        <div class="space-y-2">
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">手机号</label>
          <input type="text" :value="user?.phone || '-'" disabled
            class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-gray-50 dark:bg-gray-600 text-gray-500 dark:text-gray-300" />
        </div>
        <div class="space-y-2">
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">用户角色</label>
          <select v-model="localRole"
            class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-indigo-500 dark:bg-gray-700 dark:text-gray-200">
            <option v-for="(label, role) in roleMap" :key="role" :value="Number(role)">
              {{ label }}
            </option>
          </select>
        </div>
        <div class="space-y-2">
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">用户 ID</label>
          <input type="text" :value="user?.id" disabled
            class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-gray-50 dark:bg-gray-600 text-gray-500 dark:text-gray-300" />
        </div>
      </div>
    </div>

    <!-- 租户绑定信息 -->
    <div>
      <h4 class="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">租户绑定信息</h4>
      
      <!-- Has Binding -->
      <div v-if="user?.tenantBinding" class="rounded-lg border border-gray-200 dark:border-gray-700 p-4 bg-gray-50 dark:bg-gray-900/50">
        <dl class="grid grid-cols-2 gap-4">
          <div>
            <dt class="text-sm text-gray-600 dark:text-gray-400 mb-1">租户名称</dt>
            <dd class="text-sm font-medium text-gray-900 dark:text-gray-100">
              {{ user.tenantBinding.tenantName }}
            </dd>
          </div>
          <div>
            <dt class="text-sm text-gray-600 dark:text-gray-400 mb-1">租户 ID</dt>
            <dd class="text-sm font-medium text-gray-900 dark:text-gray-100">
              {{ user.tenantBinding.tenantId }}
            </dd>
          </div>
          <div class="col-span-2">
            <dt class="text-sm text-gray-600 dark:text-gray-400 mb-1">绑定时间</dt>
            <dd class="text-sm font-medium text-gray-900 dark:text-gray-100">
              {{ formatDate(user.tenantBinding.boundAt) }}
            </dd>
          </div>
        </dl>
      </div>

      <!-- No Binding -->
      <div v-else class="rounded-lg border border-gray-200 dark:border-gray-700 p-4 text-center bg-gray-50 dark:bg-gray-900/50">
        <svg class="mx-auto h-10 w-10 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <p class="mt-2 text-sm font-medium text-gray-900 dark:text-gray-100">未绑定租户</p>
        <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">该用户当前未绑定到任何租户</p>
      </div>

      <!-- Info Notice -->
      <div class="mt-3 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 p-3">
        <div class="flex">
          <svg class="h-5 w-5 text-blue-400 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
            <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd" />
          </svg>
          <p class="ml-3 text-xs text-blue-700 dark:text-blue-300">
            <strong>说明:</strong> 租户绑定由租户控制台管理员在"租户控制台管理"页面进行操作。
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue';
import type { User } from '@/types/user/user';
import { UserRole } from '@/types/auth/role';

const props = defineProps<{
  user: User | null;
  roleMap: Record<UserRole, string>;
  modelValue: UserRole;
}>();

const emit = defineEmits<{
  (e: 'update:modelValue', value: UserRole): void;
}>();

const localRole = ref(props.modelValue);

/**
 * 格式化日期
 */
const formatDate = (date: string): string => {
  return new Date(date).toLocaleString('zh-CN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
};

// 监听本地角色变化，同步到父组件
watch(localRole, (newVal) => {
  emit('update:modelValue', newVal);
});

// 监听 props 变化，同步到本地
watch(
  () => props.modelValue,
  (newVal) => {
    localRole.value = newVal;
  }
);
</script>
